import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useCargosControllerListar } from '@/api/generated/admin-cargos/admin-cargos';
import {
  getUsuariosControllerListarQueryKey,
  useUsuariosControllerDefinirCargo,
} from '@/api/generated/admin-usuarios/admin-usuarios';
import type { UsuarioResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

/** Cargo cuja bio é obrigatória — o médico vê antes de escolher o advisor. */
const CARGO_EXIGE_BIO = 'assessor';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  usuario: UsuarioResponse;
}

/**
 * Troca o cargo do vínculo ativo do usuário.
 *
 * Vale no request seguinte: o guard relê o vínculo a cada requisição, então não
 * há cache a esperar nem necessidade de o usuário sair e entrar.
 */
export function CargoDoUsuarioDialogo({ aberto, aoFechar, usuario }: Props) {
  const queryClient = useQueryClient();
  const [cargoId, setCargoId] = useState<string>('');
  const [bio, setBio] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [erroBio, setErroBio] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useCargosControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: aberto } },
  );

  const definir = useUsuariosControllerDefinirCargo();

  useEffect(() => {
    if (aberto) {
      setCargoId(usuario.cargo?.id ?? '');
      setBio(usuario.bio ?? '');
      setErro(null);
      setErroBio(null);
    }
  }, [aberto, usuario]);

  // Cargo passou a valer pelo `id` no select, mas a regra de bio é por
  // `chave` — só o backend conhece o par id↔chave em definitivo, esta lista
  // já veio junto e evita um segundo request.
  const cargoSelecionado = data?.itens.find((c) => c.id === cargoId);
  const mostrarBio = cargoSelecionado?.chave === CARGO_EXIGE_BIO;

  async function salvar() {
    if (!cargoId) return;
    setErro(null);
    setErroBio(null);

    if (mostrarBio && !bio.trim()) {
      setErroBio('Bio é obrigatória para o cargo assessor.');
      return;
    }

    try {
      await definir.mutateAsync({
        id: usuario.id,
        data: { cargoId, ...(mostrarBio ? { bio: bio.trim() } : {}) },
      });
      await queryClient.invalidateQueries({ queryKey: getUsuariosControllerListarQueryKey() });
      toast.success(`Cargo de ${usuario.nomeCompleto} atualizado.`);
      aoFechar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível alterar o cargo.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cargo de {usuario.nomeCompleto}</DialogTitle>
          <DialogDescription>
            O cargo define o que o usuário pode fazer. A mudança vale na próxima ação dele.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <EstadoCarregando rotulo="Carregando cargos..." />
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : (
          <div className="space-y-4">
            {erro && (
              <Alert variant="destructive">
                <AlertDescription>{erro}</AlertDescription>
              </Alert>
            )}

            {!usuario.ambiente && (
              <Alert variant="destructive">
                <AlertDescription>
                  Este usuário não tem vínculo ativo com nenhum ambiente, então não há cargo a
                  atribuir.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="cargo" className="text-brand font-medium">
                Cargo
              </Label>
              <Select value={cargoId} onValueChange={setCargoId} disabled={!usuario.ambiente}>
                <SelectTrigger id="cargo">
                  <SelectValue placeholder="Selecione um cargo" />
                </SelectTrigger>
                <SelectContent>
                  {data.itens.map((cargo) => (
                    <SelectItem key={cargo.id} value={cargo.id}>
                      {cargo.nome} · {cargo.permissoes.length} permissões
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {mostrarBio && (
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-brand font-medium">
                  Bio <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="bio"
                  rows={4}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  maxLength={1000}
                />
                {erroBio && <p className="text-sm text-destructive">{erroBio}</p>}
                <p className="text-right text-xs text-muted-foreground">{bio.length}/1000</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar} disabled={definir.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => void salvar()}
            disabled={definir.isPending || !cargoId || cargoId === usuario.cargo?.id}
          >
            {definir.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
